import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { MessageSquare, ExternalLink } from "lucide-react";
import {
  AgentSdkThread,
  AgentSdkThreadResponseSchema,
} from "@forgepoint/shared";
import { fetchApi } from "@/lib/api";
import { useOpportunitiesStore } from "@/modules/opportunities/opportunitiesStore";

interface QuotePrimaryThreadLinkProps {
  opportunityId: string;
}

/**
 * Banner link to the agent thread that created this quote, shown at the top of the quote meta bar.
 * Resolves the quote's opportunity to its primary thread, then renders a low-key
 * "Created from thread · <name>" link that navigates into the thread so the user
 * can hop back in. Renders nothing when there is no opportunity primary thread or
 * the user cannot access it (the single-thread endpoint enforces that access check).
 *
 * @param opportunityId - The opportunity the quote belongs to, whose primary thread is shown
 * @returns The inline thread link, or null when there is no accessible primary thread
 */
export function QuotePrimaryThreadLink({
  opportunityId,
}: QuotePrimaryThreadLinkProps) {
  const navigate = useNavigate();
  const opportunity = useOpportunitiesStore((state) =>
    state.opportunities.get(opportunityId),
  );
  const getOpportunity = useOpportunitiesStore((state) => state.getOpportunity);
  const [thread, setThread] = useState<AgentSdkThread | null>(null);

  useEffect(() => {
    if (!opportunity) {
      getOpportunity(opportunityId);
    }
  }, [opportunityId, opportunity, getOpportunity]);

  const primaryThreadId = opportunity?.primaryThreadId ?? null;

  useEffect(() => {
    if (!primaryThreadId) {
      setThread(null);
      return;
    }

    let stale = false;
    const fetchThread = async () => {
      const response = await fetchApi(
        `standard-agent/threads/${primaryThreadId}`,
        "GET",
      );
      if (stale) {
        return;
      }
      setThread(
        response.ok
          ? AgentSdkThreadResponseSchema.parse(response.data).thread
          : null,
      );
    };

    fetchThread();

    return () => {
      stale = true;
    };
  }, [primaryThreadId]);

  if (!thread) {
    return null;
  }

  return (
    <div className="border-b bg-muted/50 px-3 sm:px-4 py-2">
      <button
        type="button"
        onClick={() => navigate(`/standard-agent?threadId=${thread.id}`)}
        className="group inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
      >
        <MessageSquare className="h-3 w-3" />
        <span>Created from thread</span>
        <span className="text-muted-foreground/40">·</span>
        <span className="font-medium text-foreground/80 group-hover:underline">
          {thread.name || "Untitled thread"}
        </span>
        <ExternalLink className="h-2.5 w-2.5 opacity-0 transition-opacity group-hover:opacity-60" />
      </button>
    </div>
  );
}
